import { z } from 'zod';
/** A trained model registered for a product. Binary lives in MinIO. */
export const ModelSchema = z.object({
    /** Running beside the live model on real traffic, deciding nothing. */
    shadow: z.boolean().default(false),
    shadowSince: z.string().nullable().default(null),
    /** Which question this model answers — see ./tasks.ts. Activating a
        classifier where an anomaly detector is expected produces a number in the
        right range with the opposite meaning. */
    task: z.enum(['anomaly', 'classification']).default('anomaly'),
    /** How to read its raw output. New variants become a manifest value rather
        than a code change per customer. */
    outputType: z
        .enum(['anomaly_score', 'logits', 'probabilities', 'embedding', 'multilabel', 'ordinal'])
        .default('anomaly_score'),
    /** From calibration. Null means nobody has calibrated it, which is different
        from calibrated-and-found-to-be-1. */
    temperature: z.number().positive().nullable().default(null),
    calibratedAt: z.string().nullable().default(null),
    id: z.number().int().positive(),
    productId: z.number().int().positive(),
    version: z.string(),
    objectKey: z.string(),
    backend: z.enum(['onnx', 'stub']),
    /** Free-form evaluation metrics from training (auroc, f1, threshold, …). */
    metrics: z.record(z.unknown()).default({}),
    notes: z.string().nullable(),
    sizeBytes: z.number().int().nonnegative(),
    active: z.boolean(),
    createdAt: z.string(),
});
/** Multipart upload: `model` file + these form fields. */
export const UploadModelFormSchema = z.object({
    productId: z.coerce.number().int().positive(),
    version: z.string().min(1),
    notes: z.string().optional(),
    metrics: z.string().optional(), // JSON string
});
// ── Dataset export ──────────────────────────────────────────
export const DatasetExportStatsSchema = z.object({
    trainGood: z.number().int().nonnegative(),
    testGood: z.number().int().nonnegative(),
    testBad: z.number().int().nonnegative(),
    masks: z.number().int().nonnegative(),
    defectTypes: z.array(z.string()),
    /** UNSURE labels are excluded: unresolved images must not become training truth. */
    unsureExcluded: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
});
export const DatasetExportSchema = z.object({
    id: z.string().uuid(),
    productId: z.number().int().positive(),
    /** MinIO prefix holding the materialized dataset. */
    prefix: z.string(),
    stats: DatasetExportStatsSchema,
    createdAt: z.string(),
});
export const CreateExportSchema = z.object({
    productId: z.coerce.number().int().positive(),
    /** Fraction of GOOD images held out for the test split. */
    testSplit: z.number().min(0).max(0.9).default(0.2),
    /**
     * Train from unlabelled images, treating the batch as mostly-normal.
     *
     * The way out of a new customer's chicken-and-egg: hundreds of images,
     * nothing annotated, and no model to help annotate them.
     */
    bootstrapUnlabelled: z.boolean().default(false),
});
//# sourceMappingURL=models.js.map